import { Head } from '@inertiajs/react';
import { motion } from 'framer-motion';
import Layout from '@/layouts/Layout';
import { fadeInUp, staggerContainer } from '@/hooks/use-scroll-animation';
import { Heading } from '@/components/ui/heading';
import { Badge } from '@/components/ui/badge';
import {
  ShieldCheck,
  Globe,
  Users,
  Plane,
  Lock,
  Bot,
  BadgeCheck,
  AlertCircle,
  RefreshCw,
  Building2,
  FileText,
  Mail,
  Eye,
  Clock,
  type LucideIcon,
} from 'lucide-react';

const SectionHeader = ({
  icon: Icon,
  number,
  title,
}: {
  icon: LucideIcon;
  number: string;
  title: string;
}) => (
  <Heading level={2} icon={Icon} className="mb-5">
    <span className="text-muted-foreground mr-2">{number}</span>
    {title}
  </Heading>
);

const purposes = [
  { purpose: 'Erogazione del servizio di piattaforma', basis: 'Esecuzione del contratto (art. 6.1.b)' },
  {
    purpose: 'Archiviazione e consultazione di chat e diari',
    basis: 'Finalità di cura sotto la responsabilità del Professionista (art. 9.2.h)',
  },
  {
    purpose: 'Generazione di riassunti e analisi AI a supporto del Professionista',
    basis: 'Finalità di cura sotto la responsabilità del Professionista (art. 9.2.h)',
  },
  {
    purpose: 'Monitoraggio di sicurezza per situazioni a rischio',
    basis: "Interesse vitale dell'interessato (art. 6.1.d e art. 9.2.c)",
  },
  { purpose: 'Comunicazioni di servizio', basis: 'Esecuzione del contratto (art. 6.1.b)' },
];

const accessTable = [
  { subject: 'Il tuo Professionista', data: 'Tutti i tuoi dati, inclusi i riassunti AI' },
  { subject: 'Tu (paziente)', data: 'I tuoi dati personali, chat, diari. Non i riassunti AI' },
  { subject: 'Team Emovia (amministrazione)', data: 'Solo dati aggregati e anonimizzati' },
  {
    subject: 'Safety Team Emovia',
    data: 'Accesso puntuale a specifici contenuti, esclusivamente per la tutela della tua sicurezza',
  },
];

const recipients = [
  { name: 'Amazon Web Services Inc.', service: 'Infrastruttura', location: 'Unione Europea' },
  { name: 'Cloudflare Inc.', service: 'CDN e sicurezza', location: 'Stati Uniti *' },
  { name: 'Google LLC', service: 'Archiviazione file ed email', location: 'Unione Europea' },
  { name: 'Hetzner Online GmbH', service: 'Hosting', location: 'Unione Europea' },
  { name: 'OpenAI LP', service: 'Elaborazione AI', location: 'Stati Uniti *' },
  { name: 'PostHog Inc.', service: 'Analytics', location: 'Unione Europea' },
  { name: 'Resend Inc.', service: 'Email transazionali', location: 'Stati Uniti *' },
  { name: 'Stripe Inc.', service: 'Gestione pagamenti', location: 'Stati Uniti *' },
];

const retentionTable = [
  {
    category: 'I tuoi dati (chat, diari)',
    period:
      'Per la durata del rapporto terapeutico, poi fino a 5 anni dalla conclusione, salvo diversa istruzione del Professionista',
  },
  { category: 'Dati di navigazione', period: '24 mesi' },
  { category: 'Backup', period: 'Massimo 30 giorni dalla cancellazione dei dati originali' },
];

const rights = [
  { art: 'Art. 15', label: 'Accesso', desc: 'Ottenere conferma del trattamento e copia dei tuoi dati.' },
  { art: 'Art. 16', label: 'Rettifica', desc: 'Chiedere la correzione di dati inesatti o incompleti.' },
  { art: 'Art. 17', label: 'Cancellazione', desc: 'Chiedere la cancellazione dei tuoi dati.' },
  { art: 'Art. 18', label: 'Limitazione', desc: 'Chiedere la limitazione del trattamento.' },
  { art: 'Art. 20', label: 'Portabilità', desc: 'Ricevere i tuoi dati in formato strutturato.' },
  { art: 'Art. 21', label: 'Opposizione', desc: 'Opporti al trattamento basato su legittimo interesse.' },
  {
    art: '—',
    label: 'Revoca del consenso',
    desc: 'In qualsiasi momento, senza pregiudicare i trattamenti precedenti.',
  },
];

const PlatformPrivacyPolicy = () => {
  return (
    <Layout>
      <Head title="Informativa Privacy — Piattaforma Emovia" />

      {/* Hero */}
      <section className="py-16 md:py-20 bg-blue-lighter/30">
        <div className="container">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl mx-auto"
          >
            <motion.div variants={fadeInUp} transition={{ duration: 0.5 }} className="mb-6">
              <Badge
                variant="outline"
                className="bg-primary/10 border-primary/20 text-primary/80 rounded-full px-4 py-1.5"
              >
                Ultimo aggiornamento: 25 marzo 2026
              </Badge>
            </motion.div>
            <motion.div variants={fadeInUp} transition={{ duration: 0.5 }}>
              <Heading level={1} font="display" className="text-primary mb-3">
                Informativa sul trattamento dei dati personali — Piattaforma Emovia
              </Heading>
            </motion.div>
            <motion.p variants={fadeInUp} transition={{ duration: 0.5 }} className="text-lg text-muted-foreground">
              ai sensi degli artt. 13-14 del Regolamento (UE) 2016/679 (&ldquo;GDPR&rdquo;)
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Main content */}
      <section className="pb-24">
        <div className="container">
          <div className="max-w-3xl mx-auto">

            {/* 1. Titolare e Responsabile */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border"
            >
              <SectionHeader icon={Building2} number="1." title="Titolare e Responsabile del trattamento" />
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Il <span className="text-foreground">Titolare del trattamento</span> dei tuoi dati personali e
                  sanitari è il tuo <span className="text-foreground">Professionista</span> (psicologo), iscritto
                  all&apos;albo professionale, che ti segue nel percorso terapeutico.
                </p>
                <p>
                  <span className="text-foreground">Emovia di Daniele Di Bernardo</span> agisce in qualità di{' '}
                  <span className="text-foreground">Responsabile del trattamento (art. 28 GDPR)</span> per conto del
                  Professionista, limitatamente ai trattamenti necessari all&apos;erogazione dei servizi della
                  piattaforma.
                </p>
                <div className="border border-border rounded-lg p-4 space-y-1">
                  <p className="text-foreground font-medium">Emovia di Daniele Di Bernardo</p>
                  <p>Viale Sicilia 31, 27100 Pavia (PV), Italia</p>
                  <p>P.IVA: IT03023520186</p>
                  <a
                    href="mailto:privacy@emovia.com"
                    aria-label="Invia email a privacy@emovia.com"
                    className="inline-flex items-center gap-2 text-primary underline-offset-4 hover:underline mt-1"
                  >
                    <Mail className="h-4 w-4" aria-hidden />
                    privacy@emovia.com
                  </a>
                </div>
              </div>
            </motion.div>

            {/* 2. Categorie di dati */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border bg-blue-lighter/20 -mx-4 px-4 sm:-mx-6 sm:px-6 rounded-lg"
            >
              <SectionHeader icon={FileText} number="2." title="Categorie di dati trattati" />
              <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                <li><span className="text-foreground">Dati identificativi:</span> nome, cognome, email</li>
                <li>
                  <span className="text-foreground">Dati relativi alla salute (art. 9 GDPR):</span> contenuto delle
                  chat terapeutiche, diari personali
                </li>
                <li>File e documenti eventualmente caricati nella piattaforma</li>
                <li><span className="text-foreground">Dati di navigazione:</span> indirizzo IP, browser, dispositivo</li>
              </ul>
            </motion.div>

            {/* 3. Natura del conferimento */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border"
            >
              <SectionHeader icon={AlertCircle} number="3." title="Natura del conferimento dei dati" />
              <p className="text-muted-foreground">
                Il conferimento dei dati personali è necessario per l&apos;utilizzo della piattaforma Emovia e per la
                gestione del rapporto terapeutico con il tuo Professionista. Il mancato conferimento comporta
                l&apos;impossibilità di utilizzare i servizi della piattaforma.
              </p>
            </motion.div>

            {/* 4. Finalità e basi giuridiche */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border bg-blue-lighter/20 -mx-4 px-4 sm:-mx-6 sm:px-6 rounded-lg"
            >
              <SectionHeader icon={ShieldCheck} number="4." title="Finalità e basi giuridiche" />
              <div className="overflow-x-auto mb-4">
                <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
                  <thead>
                    <tr className="bg-muted/40 text-left border-b border-border">
                      <th scope="col" className="px-4 py-3 text-foreground">Finalità</th>
                      <th scope="col" className="px-4 py-3 text-foreground">Base giuridica</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {purposes.map(({ purpose, basis }) => (
                      <tr key={purpose} className="hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-3 text-foreground">{purpose}</td>
                        <td className="px-4 py-3 text-muted-foreground">{basis}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-muted-foreground text-sm border-t border-border/50 pt-4">
                I riassunti e le analisi generate tramite AI sono strumenti di supporto destinati esclusivamente al tuo
                Professionista e non sono visibili a te come paziente. Il Professionista mantiene il pieno controllo
                clinico.
              </p>
            </motion.div>

            {/* 5. Accesso ai dati */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border"
            >
              <SectionHeader icon={Eye} number="5." title="Accesso ai dati" />
              <div className="overflow-x-auto mb-4">
                <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
                  <thead>
                    <tr className="bg-muted/40 text-left border-b border-border">
                      <th scope="col" className="px-4 py-3 text-foreground">Soggetto</th>
                      <th scope="col" className="px-4 py-3 text-foreground">Dati accessibili</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {accessTable.map(({ subject, data }) => (
                      <tr key={subject} className="hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-3 text-foreground">{subject}</td>
                        <td className="px-4 py-3 text-muted-foreground">{data}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-muted-foreground text-sm">
                Nessun operatore Emovia accede ai tuoi dati sanitari per finalità diverse dalla sicurezza degli utenti.
              </p>
            </motion.div>

            {/* 6. Destinatari */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border bg-blue-lighter/20 -mx-4 px-4 sm:-mx-6 sm:px-6 rounded-lg"
            >
              <SectionHeader icon={Users} number="6." title="Destinatari dei dati" />
              <div className="overflow-x-auto mb-4">
                <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
                  <thead>
                    <tr className="bg-muted/40 text-left border-b border-border">
                      <th scope="col" className="px-4 py-3 text-foreground">Fornitore</th>
                      <th scope="col" className="px-4 py-3 text-foreground">Servizio</th>
                      <th scope="col" className="px-4 py-3 text-foreground">Sede server</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {recipients.map(({ name, service, location }) => (
                      <tr key={name} className="hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-3 text-foreground">{name}</td>
                        <td className="px-4 py-3 text-muted-foreground">{service}</td>
                        <td className="px-4 py-3 text-muted-foreground">{location}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-muted-foreground text-sm">
                I dati non vengono venduti né ceduti a terzi. I dati elaborati tramite OpenAI non vengono utilizzati per
                l&apos;addestramento dei modelli.
              </p>
            </motion.div>

            {/* 7. Trasferimenti verso Paesi terzi */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border"
            >
              <SectionHeader icon={Plane} number="7." title="Trasferimenti verso Paesi terzi" />
              <p className="text-muted-foreground mb-4">
                I fornitori contrassegnati con (*) hanno sede negli Stati Uniti. I trasferimenti avvengono sulla base
                di:
              </p>
              <ul className="space-y-3 text-muted-foreground mb-4">
                <li className="flex items-start gap-2">
                  <BadgeCheck className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" aria-hidden />
                  <span>
                    <span className="text-foreground">Clausole Contrattuali Standard (SCC)</span>{' '}
                    approvate dalla Commissione Europea (decisione 2021/914).
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <BadgeCheck className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" aria-hidden />
                  <span>
                    <span className="text-foreground">Data Processing Agreement (DPA)</span>{' '}
                    con ciascun fornitore.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <BadgeCheck className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" aria-hidden />
                  <span>
                    <span className="text-foreground">EU-US Data Privacy Framework</span>{' '}
                    (decisione di adeguatezza 2023/1795), ove applicabile.
                  </span>
                </li>
              </ul>
              <p className="text-muted-foreground text-sm border-t border-border/50 pt-4">
                Per i dati relativi alla salute elaborati tramite OpenAI: i dati vengono trasmessi tramite API con
                contratto enterprise; la data retention sui sistemi OpenAI è conforme al relativo DPA; i dati non
                vengono utilizzati per l&apos;addestramento dei modelli di intelligenza artificiale.
              </p>
            </motion.div>

            {/* 8. Conservazione */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border bg-blue-lighter/20 -mx-4 px-4 sm:-mx-6 sm:px-6 rounded-lg"
            >
              <SectionHeader icon={Clock} number="8." title="Conservazione dei dati" />
              <div className="overflow-x-auto mb-4">
                <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
                  <thead>
                    <tr className="bg-muted/40 text-left border-b border-border">
                      <th scope="col" className="px-4 py-3 text-foreground">Categoria</th>
                      <th scope="col" className="px-4 py-3 text-foreground">Periodo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {retentionTable.map(({ category, period }) => (
                      <tr key={category} className="hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-3 text-foreground">{category}</td>
                        <td className="px-4 py-3 text-muted-foreground">{period}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-muted-foreground text-sm">
                Al termine del rapporto con il Professionista, i tuoi dati vengono conservati o cancellati secondo le
                istruzioni del Professionista, nel rispetto degli obblighi di legge.
              </p>
            </motion.div>

            {/* 9. Misure di sicurezza */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border"
            >
              <SectionHeader icon={Lock} number="9." title="Misure di sicurezza" />
              <ul className="space-y-3 text-muted-foreground">
                {[
                  'Cifratura dei dati in transito e a riposo.',
                  'Accesso ai tuoi dati sanitari limitato al solo Professionista competente.',
                  'Sistemi di monitoraggio e prevenzione degli accessi non autorizzati.',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <ShieldCheck className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" aria-hidden />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* 10. Trattamenti automatizzati e AI */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border bg-blue-lighter/20 -mx-4 px-4 sm:-mx-6 sm:px-6 rounded-lg"
            >
              <SectionHeader icon={Bot} number="10." title="Trattamenti automatizzati e AI" />
              <div className="space-y-3 text-muted-foreground">
                <p>
                  La piattaforma utilizza sistemi di intelligenza artificiale per generare riassunti e analisi a
                  supporto dell&apos;attività clinica del tuo Professionista. Tali elaborazioni:
                </p>
                <ul className="space-y-2">
                  {[
                    'Sono strumenti di supporto e non sostituiscono il giudizio clinico del Professionista.',
                    'Non producono decisioni automatizzate con effetti giuridici su di te.',
                    'Sono visibili esclusivamente al tuo Professionista, non a te.',
                    'Vengono generate tramite il servizio di OpenAI (indicato tra i destinatari dei dati).',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <BadgeCheck className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" aria-hidden />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p>Nessuna decisione clinica viene presa in modo automatizzato dalla piattaforma.</p>
              </div>
            </motion.div>

            {/* 11. Diritti */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border"
            >
              <SectionHeader icon={BadgeCheck} number="11." title="I tuoi diritti" />
              <ul className="divide-y divide-border/60 mb-6">
                {rights.map(({ art, label, desc }) => (
                  <li key={label} className="flex items-baseline gap-4 py-3">
                    <span className="text-muted-foreground text-sm w-16 flex-shrink-0">{art}</span>
                    <div>
                      <span className="text-foreground">{label}</span>
                      <p className="text-muted-foreground mt-0.5">{desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="space-y-2 text-muted-foreground">
                <p>
                  Per esercitare i tuoi diritti relativi ai dati trattati nell&apos;ambito del rapporto terapeutico,
                  rivolgiti al tuo Professionista, che è il Titolare del trattamento.
                </p>
                <p>
                  Per questioni relative al funzionamento della piattaforma, puoi scrivere a{' '}
                  <a
                    href="mailto:privacy@emovia.com"
                    className="text-primary underline-offset-4 hover:underline"
                  >
                    privacy@emovia.com
                  </a>
                  .
                </p>
                <p>Risponderemo entro 30 giorni dalla richiesta.</p>
              </div>
            </motion.div>

            {/* 12. Reclamo */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10 border-b border-border bg-blue-lighter/20 -mx-4 px-4 sm:-mx-6 sm:px-6 rounded-lg"
            >
              <SectionHeader icon={AlertCircle} number="12." title="Diritto di reclamo" />
              <p className="text-muted-foreground mb-4">
                Hai il diritto di proporre reclamo al Garante per la Protezione dei Dati Personali:
              </p>
              <div className="text-muted-foreground space-y-1">
                <p className="text-foreground">Garante per la Protezione dei Dati Personali</p>
                <p>Piazza Venezia 11, 00187 Roma</p>
                <div className="pt-2 space-y-1.5">
                  <a
                    href="https://www.garanteprivacy.it"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-primary underline-offset-4 hover:underline"
                  >
                    <Globe className="h-4 w-4" aria-hidden />
                    www.garanteprivacy.it
                  </a>
                  <a
                    href="mailto:protocollo@gpdp.it"
                    aria-label="Invia email a protocollo@gpdp.it"
                    className="flex items-center gap-2 text-primary underline-offset-4 hover:underline"
                  >
                    <Mail className="h-4 w-4" aria-hidden />
                    protocollo@gpdp.it
                  </a>
                  <a
                    href="mailto:protocollo@pec.gpdp.it"
                    aria-label="Invia email a protocollo@pec.gpdp.it"
                    className="flex items-center gap-2 text-primary underline-offset-4 hover:underline"
                  >
                    <Mail className="h-4 w-4" aria-hidden />
                    protocollo@pec.gpdp.it (PEC)
                  </a>
                </div>
              </div>
            </motion.div>

            {/* 13. Modifiche */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
              transition={{ duration: 0.5 }}
              className="py-10"
            >
              <SectionHeader icon={RefreshCw} number="13." title="Modifiche alla presente informativa" />
              <p className="text-muted-foreground">
                Il contenuto della presente informativa può essere aggiornato. La data di ultimo aggiornamento è
                indicata in cima al documento. In caso di modifiche sostanziali, ne daremo comunicazione tramite la
                piattaforma.
              </p>
            </motion.div>

          </div>

          {/* Contatti privacy */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
            transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto mt-10"
          >
            <div className="bg-primary/5 border border-primary/20 rounded-2xl p-8 text-center">
              <Mail className="h-7 w-7 text-primary mx-auto mb-4" aria-hidden />
              <Heading level={3} className="mb-2">
                Hai domande sulla tua privacy?
              </Heading>
              <p className="text-muted-foreground mb-5">Scrivici. Ti rispondiamo entro 30 giorni.</p>
              <a
                href="mailto:privacy@emovia.com"
                aria-label="Invia email a privacy@emovia.com"
                className="inline-flex items-center gap-2 text-primary underline-offset-4 hover:underline"
              >
                <Mail className="h-4 w-4" aria-hidden />
                privacy@emovia.com
              </a>
            </div>
          </motion.div>

        </div>
      </section>
    </Layout>
  );
};

export default PlatformPrivacyPolicy;
